import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { calculateSavingsState } from '@theme/gio-savings-logic';

/**
 * @param {string} template
 * @param {Record<string, string | number>} values
 * @returns {string}
 */
function interpolate(template, values) {
  let output = template || '';

  for (const [key, value] of Object.entries(values)) {
    output = output.split(`__${key}__`).join(String(value));
  }

  return output;
}

/**
 * @param {HTMLElement} element
 * @param {number} cents
 * @param {string} currency
 * @returns {string}
 */
function moneyFor(element, cents, currency) {
  const moneyFormat = element.dataset.moneyFormat || '${{amount}}';

  try {
    return formatMoney(cents, moneyFormat, currency);
  } catch (_error) {
    const locale = document.documentElement.lang || 'en';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }
}

class GioSavingsWidget extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {HTMLDetailsElement | null} */
  #details = null;

  connectedCallback() {
    this.#details = this.querySelector('details');
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown);
    document.addEventListener('keydown', this.#handleKeyDown);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener('pointerdown', this.#handleOutsidePointerDown);
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleCartUpdate = (event) => {
    if (!event.promise) {
      this.#refresh();
      return;
    }

    Promise.resolve(event.promise)
      .catch(() => null)
      .finally(() => this.#refresh());
  };

  /** @param {PointerEvent} event */
  #handleOutsidePointerDown = (event) => {
    if (!this.#details?.open || !(event.target instanceof Node) || this.contains(event.target)) return;
    this.#details.open = false;
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#details?.open) return;
    this.#details.open = false;
    this.#details.querySelector('summary')?.focus();
  };

  async #refresh() {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const url = new URL(this.dataset.cartUrl || '/cart.js', window.location.origin);
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.#fetchController.signal,
      });

      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      this.#render(await response.json());
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[gio-savings] Cart state could not be refreshed.', error);
    }
  }

  /** @param {Record<string, unknown>} cart */
  #render(cart) {
    const walletRate = Number(this.dataset.walletRate || 15);
    const state = calculateSavingsState(cart, walletRate);
    const currency = String(cart.currency || this.dataset.currency || 'USD');
    const compact = this.querySelector('[data-gio-savings-compact]');
    const summary = this.querySelector('.gio-savings__summary');
    const mobileBadge = this.querySelector('[data-gio-savings-mobile-badge]');
    const message = this.querySelector('[data-gio-savings-message]');
    const savings = this.querySelector('[data-gio-savings-amount]');
    const wallet = this.querySelector('[data-gio-savings-wallet]');
    const cta = this.querySelector('[data-gio-savings-cta]');
    const savingsAmount = moneyFor(this, state.savingsCents, currency);
    const compactMessage = this.#compactMessage(state, savingsAmount);

    document.documentElement.dataset.gioSavingsItemCount = String(state.itemCount);
    document.documentElement.dataset.gioSavingsAppliedTier = String(state.appliedTierPercent);
    document.dispatchEvent(
      new CustomEvent('gio:savings-state', {
        detail: {
          itemCount: state.itemCount,
          appliedTierPercent: state.appliedTierPercent,
        },
      })
    );

    if (compact) compact.textContent = compactMessage;
    if (summary instanceof HTMLElement) summary.setAttribute('aria-label', compactMessage);
    if (mobileBadge) mobileBadge.textContent = this.#mobileBadge(state);
    if (message) message.textContent = this.#expandedMessage(state);

    if (savings instanceof HTMLElement) {
      savings.hidden = state.savingsCents <= 0;
      savings.textContent = interpolate(this.dataset.savedTemplate || '', {
        amount: savingsAmount,
      });
    }

    if (wallet instanceof HTMLElement) {
      wallet.textContent = interpolate(this.dataset.walletTemplate || '', {
        amount: moneyFor(this, state.walletCents, currency),
        rate: walletRate,
      });
    }

    if (cta instanceof HTMLAnchorElement) {
      cta.hidden = state.itemCount >= 3;
      cta.textContent = state.itemCount === 0 ? this.dataset.ctaShop || '' : this.dataset.ctaFind || '';
    }

    for (const tier of [10, 15]) {
      const row = this.querySelector(`[data-gio-savings-tier="${tier}"]`);
      if (!(row instanceof HTMLElement)) continue;
      const applied = state.appliedTierPercent >= tier;
      const reached = state.tierPercent >= tier;
      row.classList.toggle('gio-savings__tier--unlocked', reached);
      row.dataset.state = applied ? 'applied' : reached ? 'reached' : 'locked';
      const tierStatus = row.querySelector('[data-gio-savings-tier-status]');
      if (tierStatus) {
        tierStatus.textContent = applied
          ? this.dataset.activeLabel || ''
          : reached
            ? this.dataset.tierReachedLabel || ''
            : this.dataset.nextLabel || '';
      }
    }

    const liveRegion = this.querySelector('[data-gio-savings-live]');
    if (liveRegion) {
      const announcement = [this.#expandedMessage(state)];

      if (state.savingsCents > 0) {
        announcement.push(interpolate(this.dataset.savedTemplate || '', { amount: savingsAmount }));
      }

      if (wallet instanceof HTMLElement) {
        announcement.push(
          interpolate(this.dataset.walletTemplate || '', {
            amount: moneyFor(this, state.walletCents, currency),
            rate: walletRate,
          })
        );
      }

      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = announcement.join(' ');
      });
    }
  }

  /**
   * @param {{ itemCount: number, savingsCents: number }} state
   * @param {string} savingsAmount
   */
  #compactMessage(state, savingsAmount) {
    if (state.itemCount >= 3) {
      if (state.appliedTierPercent < 15 || state.savingsCents <= 0) return this.dataset.compactMaxPending || '';
      return interpolate(this.dataset.compactMax || '', { amount: savingsAmount });
    }

    if (state.itemCount === 2) {
      if (state.appliedTierPercent < 10 || state.savingsCents <= 0) return this.dataset.compactTwoPending || '';
      return interpolate(this.dataset.compactTwo || '', { amount: savingsAmount });
    }

    if (state.itemCount === 1) return this.dataset.compactOne || '';
    return this.dataset.compactEmpty || '';
  }

  /** @param {{ itemCount: number, appliedTierPercent: number }} state */
  #expandedMessage(state) {
    if (state.itemCount >= 3) {
      return state.appliedTierPercent >= 15 ? this.dataset.messageMax || '' : this.dataset.messageMaxPending || '';
    }
    if (state.itemCount === 2) {
      return state.appliedTierPercent >= 10 ? this.dataset.messageTwo || '' : this.dataset.messageTwoPending || '';
    }
    if (state.itemCount === 1) return this.dataset.messageOne || '';
    return this.dataset.messageEmpty || '';
  }

  /** @param {{ itemCount: number, appliedTierPercent: number }} state */
  #mobileBadge(state) {
    if (state.itemCount >= 3) return state.appliedTierPercent >= 15 ? '15%' : '3+';
    if (state.itemCount === 2) return state.appliedTierPercent >= 10 ? '10%' : '2';
    if (state.itemCount === 1) return '+1';
    return 'MIX';
  }
}

if (!customElements.get('gio-savings-widget')) {
  customElements.define('gio-savings-widget', GioSavingsWidget);
}
